import styles from './GaugeConfig.module.scss'
import { useCallback, useEffect, useState } from 'react'
import { propExistsOnObject } from '../../utils'
import {
    FONT_FAMILIES,
    FONT_WEIGHTS,
    GAUGE_DEFAULT_VALUES,
    PIE_ORIENTATIONS,
} from '../../styleConsts'
import {
    ScoopSlider,
    ScoopSelector,
    ScoopText,
    ScoopTextField,
    ScoopColorPicker,
    ScoopSwitch,
} from '../../../../../../ui'
import { debounce, cloneDeep } from 'lodash'

export const GaugeConfig = ({ config, setConfig, chartState, chartPreferences }) => {
    const [selectedValue, setSelectedValue] = useState(
        config.styleOverrides?.gauge?.data?.[0]?.name || chartState?.categoryAxisValues?.[0] || null
    )
    const [valueFontSize, setValueFontSize] = useState(GAUGE_DEFAULT_VALUES.detail.fontSize)
    const [valueLeft, setValueLeft] = useState(GAUGE_DEFAULT_VALUES.detail.offsetCenter[0])
    const [valueTop, setValueTop] = useState(GAUGE_DEFAULT_VALUES.detail.offsetCenter[1])

    const [labelFontSize, setLabelFontSize] = useState(GAUGE_DEFAULT_VALUES.title.fontSize)
    const [labelLeft, setLabelLeft] = useState(GAUGE_DEFAULT_VALUES.title.offsetCenter[0])
    const [labelTop, setLabelTop] = useState(GAUGE_DEFAULT_VALUES.title.offsetCenter[1])
    const [axisLineWidth, setAxisLineWidth] = useState(
        GAUGE_DEFAULT_VALUES.axisLine.lineStyle.width
    )
    const [splitNumber, setSplitNumber] = useState(GAUGE_DEFAULT_VALUES.axisTick.splitNumber)
    const [tickSplitNumber, setTickSplitNumber] = useState(
        GAUGE_DEFAULT_VALUES.axisTick.splitNumber
    )
    const [axisTickDistance, setAxisTickDistance] = useState(GAUGE_DEFAULT_VALUES.axisTick.distance)
    const [axisTickWidth, setAxisTickWidth] = useState(
        GAUGE_DEFAULT_VALUES.axisTick.lineStyle.width
    )
    const [splitLineLength, setSplitLineLength] = useState(GAUGE_DEFAULT_VALUES.splitLine.length)
    const [splitLineWidth, setSplitLineWidth] = useState(
        GAUGE_DEFAULT_VALUES.splitLine.lineStyle.width
    )
    const [axisFontSize, setAxisFontSize] = useState(GAUGE_DEFAULT_VALUES.axisLabel.fontSize)

    useEffect(() => {
        setSplitNumber(getRootConfig('splitNumber'))
        setAxisLineWidth(getLineStyle('width'))
        setValueFontSize(getDetailConfig('fontSize'))
        setValueLeft(getDetailConfig('offsetCenter')[0])
        setValueTop(getDetailConfig('offsetCenter')[1])
        setLabelFontSize(getLabelConfig('fontSize'))
        setLabelLeft(getLabelConfig('offsetCenter')[0])
        setLabelTop(getLabelConfig('offsetCenter')[1])
        setTickSplitNumber(getAxisTickConfig('splitNumber'))
        setAxisTickDistance(getAxisTickConfig('distance'))
        setAxisTickWidth(getAxisTickConfig('width', true))
        setSplitLineLength(getSplitLineConfig('length'))
        setSplitLineWidth(getSplitLineConfig('width', true))
        setAxisFontSize(getAxisConfig('fontSize'))
    }, [config.styleOverrides.gauge])

    const debouncedFunc = useCallback(
        debounce((target, prop, value) => {
            const newConfig = cloneDeep(config)
            if (target) {
                newConfig.styleOverrides.gauge[target][prop] = value
            } else {
                newConfig.styleOverrides.gauge[prop] = value
            }
            setConfig(newConfig)
        }, 200),
        [config]
    )

    const debouncedLineStyleFunc = useCallback(
        debounce((prop, value) => {
            const newConfig = cloneDeep(config)
            newConfig.styleOverrides.gauge.axisLine.lineStyle[prop] = value
            setConfig(newConfig)
        }, 200),
        [config]
    )

    const debouncedAxisTickFunc = useCallback(
        debounce((prop, value) => {
            const newConfig = cloneDeep(config)
            newConfig.styleOverrides.gauge.axisTick.lineStyle[prop] = value
            setConfig(newConfig)
        }, 200),
        [config]
    )

    const debouncedSplitLineFunc = useCallback(
        debounce((prop, value) => {
            const newConfig = cloneDeep(config)
            newConfig.styleOverrides.gauge.splitLine.lineStyle[prop] = value
            setConfig(newConfig)
        }, 200),
        [config]
    )

    const getNewNumericValue = (value) => {
        const index = chartState?.categoryAxisValues?.findIndex((v) => v === value)
        if (!index) return 0
        const data = chartState?.series[0].data
        return parseFloat(((data[index] * 100) / data.reduce((a, b) => a + b, 0)).toFixed(3))
    }

    const handleValueChange = (value) => {
        setSelectedValue(value)
        const newConfig = cloneDeep(config)
        if (
            !newConfig.styleOverrides.gauge.data ||
            newConfig.styleOverrides.gauge.data.length === 0
        ) {
            newConfig.styleOverrides.gauge.data = [{ name: '', value: 0 }]
        }
        newConfig.styleOverrides.gauge.data[0].value = getNewNumericValue(value)
        newConfig.styleOverrides.gauge.data[0].name = value
        console.log(typeof getNewNumericValue(value))
        console.log(newConfig.styleOverrides.gauge.data)
        setConfig(newConfig)
    }

    const setProgressConfig = (prop, value) => {
        const newConfig = cloneDeep(config)
        if (prop === 'color') {
            newConfig.styleOverrides.gauge.progress.itemStyle[prop] = value
        } else {
            newConfig.styleOverrides.gauge.progress[prop] = value
        }
        setConfig(newConfig)
    }

    const getRootConfig = (prop) => {
        if (propExistsOnObject(config.styleOverrides.gauge, prop))
            return config.styleOverrides.gauge[prop]
        if (propExistsOnObject(chartPreferences.gauge, prop)) return chartPreferences.gauge[prop]
        return GAUGE_DEFAULT_VALUES[prop]
    }

    const setRootConfig = (prop, value) => {
        const newConfig = cloneDeep(config)
        newConfig.styleOverrides.gauge[prop] = value
        setConfig(newConfig)
    }

    const handleDebouncedRootConfig = (prop, value, setter) => {
        debouncedFunc(false, prop, value)
        setter(value)
    }

    const getProgressConfig = (prop) => {
        if (prop === 'color') {
            if (propExistsOnObject(config.styleOverrides.gauge.progress.itemStyle, prop))
                return config.styleOverrides.gauge.progress.itemStyle[prop]
            if (propExistsOnObject(chartPreferences.gauge?.progress?.itemStyle, prop))
                return chartPreferences.gauge.progress.itemStyle[prop]
            return GAUGE_DEFAULT_VALUES.progress.itemStyle[prop]
        } else {
            if (propExistsOnObject(config.styleOverrides.gauge.progress, prop))
                return config.styleOverrides.gauge.progress[prop]
            if (propExistsOnObject(chartPreferences.gauge?.progress, prop))
                return chartPreferences.gauge.progress[prop]
            return GAUGE_DEFAULT_VALUES.progress[prop]
        }
    }

    const setPointerConfig = (prop, value) => {
        const newConfig = cloneDeep(config)
        if (prop === 'color') {
            newConfig.styleOverrides.gauge.pointer.itemStyle[prop] = value
        } else {
            newConfig.styleOverrides.gauge.pointer[prop] = value
        }
        setConfig(newConfig)
    }

    const getPointerConfig = (prop) => {
        if (prop === 'color') {
            if (propExistsOnObject(config.styleOverrides.gauge.pointer.itemStyle, prop))
                return config.styleOverrides.gauge.pointer.itemStyle[prop]
            if (propExistsOnObject(chartPreferences.gauge?.pointer?.itemStyle, prop))
                return chartPreferences.gauge.pointer.itemStyle[prop]
            return GAUGE_DEFAULT_VALUES.pointer.itemStyle[prop]
        } else {
            if (propExistsOnObject(config.styleOverrides.gauge.pointer, prop))
                return config.styleOverrides.gauge.pointer[prop]
            if (propExistsOnObject(chartPreferences.gauge?.pointer, prop))
                return chartPreferences.gauge.pointer[prop]
            return GAUGE_DEFAULT_VALUES.pointer[prop]
        }
    }

    const setEmphasis = (prop, value) => {
        const newConfig = cloneDeep(config)
        newConfig.styleOverrides.gauge.emphasis[prop] = value
        setConfig(newConfig)
    }

    const getEmphasis = (prop) => {
        if (propExistsOnObject(config.styleOverrides.gauge.emphasis, prop))
            return config.styleOverrides.gauge.emphasis[prop]
        if (propExistsOnObject(chartPreferences.gauge?.emphasis, prop))
            return chartPreferences.gauge.emphasis[prop]
        return GAUGE_DEFAULT_VALUES.emphasis[prop]
    }

    const getPieOrientationValue = () => {
        const startAngle = getRootConfig('startAngle')
        const endAngle = getRootConfig('endAngle')
        if (startAngle === 90 && endAngle === 270) {
            return 'right'
        } else if (startAngle === 360 && endAngle === 180) {
            return 'bottom'
        } else if (startAngle === 180 && endAngle === 360) {
            return 'top'
        } else if (startAngle === 225 && endAngle === -45) {
            return 'full'
        } else {
            return 'left'
        }
    }

    const setPieOrientationValue = (value) => {
        const newConfig = cloneDeep(config)
        switch (value) {
            case 'top':
                newConfig.styleOverrides.gauge.startAngle = 180
                newConfig.styleOverrides.gauge.endAngle = 360
                break
            case 'full':
                newConfig.styleOverrides.gauge.startAngle = 225
                newConfig.styleOverrides.gauge.endAngle = -45
                break
            case 'right':
                newConfig.styleOverrides.gauge.startAngle = 90
                newConfig.styleOverrides.gauge.endAngle = 270
                break
            case 'bottom':
                newConfig.styleOverrides.gauge.startAngle = 360
                newConfig.styleOverrides.gauge.endAngle = 180
                break
            case 'left':
                newConfig.styleOverrides.gauge.startAngle = 270
                newConfig.styleOverrides.gauge.endAngle = 90
                break
        }
        setConfig(newConfig)
    }

    const setDetailConfig = (prop, value) => {
        const newConfig = cloneDeep(config)
        newConfig.styleOverrides.gauge.detail[prop] = value
        setConfig(newConfig)
    }

    const handleDebouncedSetDetailConfig = (prop, value, setter) => {
        debouncedFunc('detail', prop, value)
        setter(value)
    }

    const handleDebouncedValuePosition = (axis, value) => {
        debouncedFunc('detail', 'offsetCenter', value)
        if (axis === 'left') setValueLeft(value[0])
        else setValueTop(value[1])
    }

    const getDetailConfig = (prop) => {
        if (propExistsOnObject(config.styleOverrides.gauge.detail, prop))
            return config.styleOverrides.gauge.detail[prop]
        if (propExistsOnObject(chartPreferences.gauge?.detail, prop))
            return chartPreferences.gauge.detail[prop]
        return GAUGE_DEFAULT_VALUES.detail[prop]
    }

    const setLineStyle = (prop, value) => {
        const newConfig = cloneDeep(config)
        if (prop === 'color') {
            newConfig.styleOverrides.gauge.axisLine.lineStyle[prop] = [[1, value]]
        } else newConfig.styleOverrides.gauge.axisLine.lineStyle[prop] = value
        setConfig(newConfig)
    }

    const handleDebouncedLineStyle = (prop, value, setter) => {
        debouncedLineStyleFunc(prop, value)
        setter(value)
    }

    const getLineStyle = (prop) => {
        if (prop === 'color') {
            if (propExistsOnObject(config.styleOverrides.gauge.axisLine.lineStyle, prop))
                return config.styleOverrides.gauge.axisLine.lineStyle[prop][1]
            if (propExistsOnObject(chartPreferences.gauge?.axisLine?.lineStyle, prop))
                return chartPreferences.gauge.axisLine.lineStyle[prop][1]
            return GAUGE_DEFAULT_VALUES.axisLine.lineStyle[prop][1]
        }
        if (propExistsOnObject(config.styleOverrides.gauge.axisLine.lineStyle, prop))
            return config.styleOverrides.gauge.axisLine.lineStyle[prop]
        if (propExistsOnObject(chartPreferences.gauge?.axisLine?.lineStyle, prop))
            return chartPreferences.gauge.axisLine.lineStyle[prop]
        return GAUGE_DEFAULT_VALUES.axisLine.lineStyle[prop]
    }

    const getLabelConfig = (prop) => {
        if (propExistsOnObject(config.styleOverrides.gauge.title, prop))
            return config.styleOverrides.gauge.title[prop]
        if (propExistsOnObject(chartPreferences.gauge?.title, prop))
            return chartPreferences.gauge.title[prop]
        return GAUGE_DEFAULT_VALUES.title[prop]
    }

    const getAxisTickConfig = (prop, lineStyle) => {
        if (lineStyle) {
            if (propExistsOnObject(config.styleOverrides.gauge.axisTick.lineStyle, prop))
                return config.styleOverrides.gauge.axisTick.lineStyle[prop]
            if (propExistsOnObject(chartPreferences.gauge?.axisTick?.lineStyle, prop))
                return chartPreferences.gauge.axisTick.lineStyle[prop]
            return GAUGE_DEFAULT_VALUES.axisTick.lineStyle[prop]
        }
        if (propExistsOnObject(config.styleOverrides.gauge.axisTick, prop))
            return config.styleOverrides.gauge.axisTick[prop]
        if (propExistsOnObject(chartPreferences.gauge?.axisTick, prop))
            return chartPreferences.gauge.axisTick[prop]
        return GAUGE_DEFAULT_VALUES.axisTick[prop]
    }

    const setSplitLineConfig = (prop, value, lineStyle) => {
        const newConfig = cloneDeep(config)
        if (lineStyle) {
            newConfig.styleOverrides.gauge.splitLine.lineStyle[prop] = value
        } else {
            newConfig.styleOverrides.gauge.splitLine[prop] = value
        }
        setConfig(newConfig)
    }

    const handleDebouncedSplitLineConfig = (prop, value, setter, lineStyle) => {
        if (lineStyle) {
            debouncedSplitLineFunc(prop, value)
        } else {
            debouncedFunc('splitLine', prop, value)
        }
        setter(value)
    }

    const setLabelConfig = (prop, value) => {
        const newConfig = cloneDeep(config)
        newConfig.styleOverrides.gauge.title[prop] = value
        setConfig(newConfig)
    }

    const setAxisTickConfig = (prop, value, lineStyle) => {
        const newConfig = cloneDeep(config)
        if (lineStyle) {
            newConfig.styleOverrides.gauge.axisTick.lineStyle[prop] = value
        } else {
            newConfig.styleOverrides.gauge.axisTick[prop] = value
        }
        setConfig(newConfig)
    }

    const handleDebouncedTickConfig = (prop, value, setter, lineStyle) => {
        if (lineStyle) {
            debouncedAxisTickFunc(prop, value)
        } else {
            debouncedFunc('axisTick', prop, value)
        }
        setter(value)
    }

    const getSplitLineConfig = (prop, lineStyle) => {
        if (lineStyle) {
            if (propExistsOnObject(config.styleOverrides.gauge.splitLine.lineStyle, prop))
                return config.styleOverrides.gauge.splitLine.lineStyle[prop]
            if (propExistsOnObject(chartPreferences.gauge?.splitLine?.lineStyle, prop))
                return chartPreferences.gauge.splitLine.lineStyle[prop]
            return GAUGE_DEFAULT_VALUES.splitLine.lineStyle[prop]
        }
        if (propExistsOnObject(config.styleOverrides.gauge.splitLine, prop))
            return config.styleOverrides.gauge.splitLine[prop]
        if (propExistsOnObject(chartPreferences.gauge?.splitLine, prop))
            return chartPreferences.gauge.splitLine[prop]
        return GAUGE_DEFAULT_VALUES.splitLine[prop]
    }

    const handleDebouncedLabelConfig = (prop, value, setter) => {
        debouncedFunc('title', prop, value)
        setter(value)
    }

    const handleDebouncedLabelPosition = (axis, value) => {
        debouncedFunc('title', 'offsetCenter', value)
        if (axis === 'left') setLabelLeft(value[0])
        else setLabelTop(value[1])
    }

    const getAxisConfig = (prop) => {
        if (propExistsOnObject(config.styleOverrides.gauge.axisLabel, prop))
            return config.styleOverrides.gauge.axisLabel[prop]
        if (propExistsOnObject(chartPreferences.gauge?.axisLabel, prop))
            return chartPreferences.gauge.axisLabel[prop]
        return GAUGE_DEFAULT_VALUES.axisLabel[prop]
    }

    const setAxisConfig = (prop, value) => {
        const newConfig = cloneDeep(config)
        newConfig.styleOverrides.gauge.axisLabel[prop] = value
        setConfig(newConfig)
    }

    const handleDebouncedAxisConfig = (prop, value, setter) => {
        debouncedFunc('axisLabel', prop, value)
        setter(value)
    }

    const showProgress = getProgressConfig('show')
    const roundCap = getProgressConfig('roundCap')
    const emphasisDisabled = getEmphasis('disabled')
    const showPointer = getPointerConfig('show')
    const showDetail = getDetailConfig('show')
    const showLabel = getLabelConfig('show')
    const showAxisTick = getAxisTickConfig('show')
    const showSplitLine = getSplitLineConfig('show')
    const showAxis = getAxisConfig('show')

    // Convert arrays to options format for ScoopSelector
    const fontFamilyOptions = FONT_FAMILIES.map((font) => ({
        value: font.value,
        label: font.label,
    }))

    const fontWeightOptions = FONT_WEIGHTS.map((weight) => ({
        value: weight.value,
        label: weight.label,
    }))

    const orientationOptions = PIE_ORIENTATIONS.map((orientation) => ({
        value: orientation.value,
        label: orientation.label,
    }))

    // Convert category axis values to options
    const categoryOptions =
        chartState?.categoryAxisValues?.map((value) => ({
            value: value,
            label: value,
        })) || []

    return (
        <div className={styles.container}>
            {chartState?.categoryAxisValues && (
                <ScoopSelector
                    label="Select an option"
                    value={selectedValue}
                    onChange={handleValueChange}
                    options={categoryOptions}
                    size="md"
                    removeMargin
                />
            )}
            <ScoopSelector
                label="Gauge orientation"
                value={getPieOrientationValue()}
                onChange={(v) => setPieOrientationValue(v)}
                options={orientationOptions}
                size="md"
                removeMargin
            />
            <div className={styles.colorPickerSection}>
                <ScoopText variant="label">Background color</ScoopText>
                <div className={styles.colorPickerContainer}>
                    <ScoopColorPicker
                        showLabel
                        value={getLineStyle('color')}
                        onChange={(v) => setLineStyle('color', v)}
                    />
                </div>
            </div>
            <ScoopSlider
                label={`Split number: ${splitNumber}`}
                value={splitNumber}
                min={1}
                max={25}
                step={1}
                onChange={(v) => handleDebouncedRootConfig('splitNumber', v, setSplitNumber)}
            />
            <div className={styles.inputRow}>
                <ScoopTextField
                    disableMargin
                    type={'number'}
                    label={'Min'}
                    value={getRootConfig('min')}
                    onChange={(e) => setRootConfig('min', e.target.value)}
                />
                <ScoopTextField
                    disableMargin
                    type={'number'}
                    label={'Max'}
                    value={getRootConfig('max')}
                    onChange={(e) => setRootConfig('max', e.target.value)}
                />
            </div>
            <ScoopSlider
                label={`Axis width: ${axisLineWidth}px`}
                value={axisLineWidth}
                min={0}
                max={50}
                step={1}
                onChange={(v) => handleDebouncedLineStyle('width', v, setAxisLineWidth)}
            />
            <div className={styles.switchRow}>
                <ScoopSwitch
                    checked={!emphasisDisabled}
                    onChange={() => setEmphasis('disabled', !emphasisDisabled)}
                />
                <ScoopText variant="label">Emphasis</ScoopText>
            </div>
            <div className={styles.collapsibleSection}>
                <div className={styles.switchRow}>
                    <ScoopSwitch
                        checked={showProgress}
                        onChange={() => setProgressConfig('show', !showProgress)}
                    />
                    <ScoopText variant="label">Show Progress</ScoopText>
                </div>
                {showProgress && (
                    <div className={styles.collapsibleContent}>
                        <div className={styles.colorPickerSection}>
                            <ScoopText variant="label">Progress color</ScoopText>
                            <div className={styles.colorPickerContainer}>
                                <ScoopColorPicker
                                    showLabel
                                    value={getProgressConfig('color')}
                                    onChange={(v) => setProgressConfig('color', v)}
                                />
                            </div>
                        </div>
                        <div className={styles.switchRow}>
                            <ScoopSwitch
                                checked={roundCap}
                                onChange={() => setProgressConfig('roundCap', !roundCap)}
                            />
                            <ScoopText variant="label">Round cap</ScoopText>
                        </div>
                    </div>
                )}
                <div className={styles.switchRow}>
                    <ScoopSwitch
                        checked={showPointer}
                        onChange={() => setPointerConfig('show', !showPointer)}
                    />
                    <ScoopText variant="label">Show Pointer</ScoopText>
                </div>
                {showPointer && (
                    <div className={styles.collapsibleContent}>
                        <div className={styles.colorPickerSection}>
                            <ScoopText variant="label">Pointer color</ScoopText>
                            <div className={styles.colorPickerContainer}>
                                <ScoopColorPicker
                                    showLabel
                                    value={getPointerConfig('color')}
                                    onChange={(v) => setPointerConfig('color', v)}
                                />
                            </div>
                        </div>
                    </div>
                )}
                <div className={styles.switchRow}>
                    <ScoopSwitch
                        checked={showAxis}
                        onChange={() => setAxisConfig('show', !showAxis)}
                    />
                    <ScoopText variant="label">Show Axis</ScoopText>
                </div>
                {showAxis && (
                    <div className={styles.collapsibleContent}>
                        <div className={styles.colorPickerSection}>
                            <ScoopText variant="label">Axis color</ScoopText>
                            <div className={styles.colorPickerContainer}>
                                <ScoopColorPicker
                                    showLabel
                                    value={getAxisConfig('color')}
                                    onChange={(v) => setAxisConfig('color', v)}
                                />
                            </div>
                        </div>
                        <ScoopSelector
                            label="Font family"
                            value={getAxisConfig('fontFamily')}
                            onChange={(v) => setAxisConfig('fontFamily', v)}
                            options={fontFamilyOptions}
                            size="md"
                            removeMargin
                        />
                        <ScoopSlider
                            label={`Font size: ${axisFontSize}px`}
                            value={axisFontSize}
                            min={8}
                            max={64}
                            step={1}
                            onChange={(v) =>
                                handleDebouncedAxisConfig('fontSize', v, setAxisFontSize)
                            }
                        />
                    </div>
                )}
                <div className={styles.switchRow}>
                    <ScoopSwitch
                        checked={showDetail}
                        onChange={() => setDetailConfig('show', !showDetail)}
                    />
                    <ScoopText variant="label">Show Value</ScoopText>
                </div>
                {showDetail && (
                    <div className={styles.collapsibleContentSmallGap}>
                        <div className={styles.colorPickerSection}>
                            <ScoopText variant="label">Value color</ScoopText>
                            <div className={styles.colorPickerContainer}>
                                <ScoopColorPicker
                                    showLabel
                                    value={getDetailConfig('color')}
                                    onChange={(v) => setDetailConfig('color', v)}
                                />
                            </div>
                        </div>
                        <ScoopSlider
                            label={`Font size: ${valueFontSize}px`}
                            value={valueFontSize}
                            min={8}
                            max={64}
                            step={1}
                            onChange={(v) =>
                                handleDebouncedSetDetailConfig('fontSize', v, setValueFontSize)
                            }
                        />
                        <ScoopSlider
                            label={`Horizontal offset: ${valueLeft}`}
                            value={parseInt(valueLeft?.replace('%', ''))}
                            onChange={(v) =>
                                handleDebouncedValuePosition('left', [v + '%', valueTop])
                            }
                            min={-100}
                            max={100}
                            step={1}
                        />
                        <ScoopSlider
                            label={`Vertical offset: ${valueTop}`}
                            value={parseInt(valueTop?.replace('%', ''))}
                            onChange={(v) =>
                                handleDebouncedValuePosition('top', [valueLeft, v + '%'])
                            }
                            min={-100}
                            max={100}
                            step={1}
                        />
                        <ScoopSelector
                            label="Font family"
                            value={getDetailConfig('fontFamily')}
                            onChange={(v) => setDetailConfig('fontFamily', v)}
                            options={fontFamilyOptions}
                            size="md"
                            removeMargin
                        />
                        <ScoopSelector
                            label="Font weight"
                            value={getDetailConfig('fontWeight')}
                            onChange={(v) => setDetailConfig('fontWeight', v)}
                            options={fontWeightOptions}
                            size="md"
                            removeMargin
                        />
                    </div>
                )}
                <div className={styles.switchRow}>
                    <ScoopSwitch
                        checked={showLabel}
                        onChange={() => setLabelConfig('show', !showLabel)}
                    />
                    <ScoopText variant="label">Show Label</ScoopText>
                </div>
                {showLabel && (
                    <div className={styles.collapsibleContentSmallGap}>
                        <div className={styles.colorPickerSection}>
                            <ScoopText variant="label">Label color</ScoopText>
                            <div className={styles.colorPickerContainer}>
                                <ScoopColorPicker
                                    showLabel
                                    value={getLabelConfig('color')}
                                    onChange={(v) => setLabelConfig('color', v)}
                                />
                            </div>
                        </div>
                        <ScoopSlider
                            label={`Font size: ${labelFontSize}px`}
                            value={labelFontSize}
                            min={8}
                            max={64}
                            step={1}
                            onChange={(v) =>
                                handleDebouncedLabelConfig('fontSize', v, setLabelFontSize)
                            }
                        />
                        <ScoopSlider
                            label={`Horizontal offset: ${labelLeft}`}
                            value={parseInt(labelLeft?.replace('%', ''))}
                            onChange={(v) =>
                                handleDebouncedLabelPosition('left', [v + '%', labelTop])
                            }
                            min={-100}
                            max={100}
                            step={1}
                        />
                        <ScoopSlider
                            label={`Vertical offset: ${labelTop}`}
                            value={parseInt(labelTop?.replace('%', ''))}
                            onChange={(v) =>
                                handleDebouncedLabelPosition('top', [labelLeft, v + '%'])
                            }
                            min={-100}
                            max={100}
                            step={1}
                        />
                        <ScoopSelector
                            label="Font family"
                            value={getLabelConfig('fontFamily')}
                            onChange={(v) => setLabelConfig('fontFamily', v)}
                            options={fontFamilyOptions}
                            size="md"
                            removeMargin
                        />
                        <ScoopSelector
                            label="Font weight"
                            value={getLabelConfig('fontWeight')}
                            onChange={(v) => setLabelConfig('fontWeight', v)}
                            options={fontWeightOptions}
                            size="md"
                            removeMargin
                        />
                    </div>
                )}
                <div className={styles.switchRow}>
                    <ScoopSwitch
                        checked={showPointer}
                        onChange={() => setPointerConfig('show', !showPointer)}
                    />
                    <ScoopText variant="label">Show Pointer</ScoopText>
                </div>
                <div className={styles.switchRow}>
                    <ScoopSwitch
                        checked={showAxisTick}
                        onChange={() => setAxisTickConfig('show', !showAxisTick)}
                    />
                    <ScoopText variant="label">Show Tick</ScoopText>
                </div>
                {showAxisTick && (
                    <div className={styles.collapsibleContentSmallGap}>
                        <ScoopSlider
                            label={`Split number: ${tickSplitNumber}`}
                            value={tickSplitNumber}
                            min={1}
                            max={25}
                            step={1}
                            onChange={(v) =>
                                handleDebouncedTickConfig('splitNumber', v, setTickSplitNumber)
                            }
                        />
                        <ScoopSlider
                            label={`Tick distance: ${axisTickDistance}`}
                            value={axisTickDistance}
                            min={0}
                            max={50}
                            step={1}
                            onChange={(v) =>
                                handleDebouncedTickConfig('distance', v, setAxisTickDistance)
                            }
                        />
                        <div className={styles.colorPickerSection}>
                            <ScoopText variant="label">Tick color</ScoopText>
                            <div className={styles.colorPickerContainer}>
                                <ScoopColorPicker
                                    showLabel
                                    value={getAxisTickConfig('color')}
                                    onChange={(v) => setAxisTickConfig('color', v, true)}
                                />
                            </div>
                        </div>
                        <ScoopSlider
                            label={`Tick width: ${axisTickWidth}px`}
                            value={axisTickWidth}
                            min={0}
                            max={20}
                            step={1}
                            onChange={(v) =>
                                handleDebouncedTickConfig('width', v, setAxisTickWidth, true)
                            }
                        />
                    </div>
                )}
                <div className={styles.switchRow}>
                    <ScoopSwitch
                        checked={showSplitLine}
                        onChange={() => setSplitLineConfig('show', !showSplitLine)}
                    />
                    <ScoopText variant="label">Show split line</ScoopText>
                </div>
                {showSplitLine && (
                    <div className={styles.collapsibleContentSmallGap}>
                        <ScoopSlider
                            label={`Length: ${splitLineLength}px`}
                            value={splitLineLength}
                            min={1}
                            max={50}
                            step={1}
                            onChange={(v) =>
                                handleDebouncedSplitLineConfig('length', v, setSplitLineLength)
                            }
                        />
                        <ScoopSlider
                            label={`Width: ${splitLineWidth}px`}
                            value={splitLineWidth}
                            min={0}
                            max={50}
                            step={1}
                            onChange={(v) =>
                                handleDebouncedSplitLineConfig('width', v, setSplitLineWidth, true)
                            }
                        />
                        <div className={styles.colorPickerSection}>
                            <ScoopText variant="label">Split line color</ScoopText>
                            <div className={styles.colorPickerContainer}>
                                <ScoopColorPicker
                                    showLabel
                                    value={getSplitLineConfig('color', true)}
                                    onChange={(v) => setSplitLineConfig('color', v, true)}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}
